import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Tooltip, Typography, useTheme } from "@mui/material";
import {
  Button,
  CircularLoader,
  DeleteIcon,
  Drawer,
  PauseIcon,
  PlayIcon,
  SkipNextIcon,
} from "components";
import {
  useBulkSkipNextCycleRecurringTickets,
  useCreateChildTicket,
  useCreateTicket,
  useCreateTicketTemplate,
  useGetBilligDefaultContract,
  useGetCustomerById,
  useGetCustomerContractSettings,
  useGetRecurringTicketById,
  useGetTicketTemplateByIdMutation,
  useGlobalStore,
  usePauseRecurringTicket,
  useResumeRecurringTicket,
  useUpdateTicketTemplate,
} from "controller";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import CustomCheckBox from "library/base/custom-checkbox";
// Phase 5 — recurring-tickets integration (additive, optional props only).
import { useRecurringTicketActions } from "pages/modules/control-center/recurring-tickets/hooks/useRecurringTicketActions";
import { useRecurringTicketFormSubmit } from "pages/modules/control-center/recurring-tickets/hooks/useRecurringTicketFormSubmit";
import { parseBudgetedHours } from "pages/modules/control-center/recurring-tickets/utils/parseBudgetedHours";
import { mergeRecurringSectionSchema } from "pages/modules/control-center/recurring-tickets/validation/recurringTicketValidation";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Controller,
  FormProvider,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import toast from "react-hot-toast";
import type {
  CreateRecurringTicketPayload,
  CreateTicketInput,
  RecurringTicketListItem,
} from "shared";
import { statusData as status } from "shared/data";
import {
  addMixpanelEvent,
  convertDoubletoHourAndMinutes,
  convertToHours,
} from "utilz/helpers";
import { v4 as uuid } from "uuid";
import * as yup from "yup";
import { AssigneeBanner } from "./components/AssigneeBanner";
import { CreateTicketMainForm } from "./components/CreateTicketMainForm";
import { CreateTicketSidebarComponent } from "./components/CreateTicketSidebarComponent";
import { TicketAssigneeButton } from "./components/TicketAssigneeButton";
import { TicketAttachmentButton } from "./components/TicketAttachmentButton";
import {
  assigneeData,
  getAttachmentData,
  resetTicketStore,
  setAssigneeData,
  setAttachmentData,
  setChecklistData,
} from "./store/newTicketStore";

yup.addMethod(yup.string, "richTextRequired", function (message) {
  return this.test("rich-text-required", message, function (value) {
    const { path, createError } = this;
    // Strip HTML tags
    const strippedValue = value ? value.replace(/<[^>]*>?/gm, "").trim() : "";
    if (!strippedValue) {
      return createError({
        path,
        message: message || "This field is required",
      });
    }
    return true;
  });
});

export type TicketCreationType =
  | "ticket"
  | "templateEdit"
  | "templateCreate"
  | "child";
export const CreateTicketDrawer = ({
  open,
  onClose,
  type = "child",
  childData,
  templateId,
  // Phase 5 — recurring-tickets additive props. All optional. Default-off preserves
  // the existing ticket/template/child flows without any behavioural change.
  initialMakeRecurring = false,
  lockMakeRecurringToggle = false,
  editMappingId,
  initialValues,
  createDrawerMode,
  editListItemData,
  createdFrom = "ticket_drawer",
}: {
  open: boolean;
  onClose: (e?: any, type?: "save" | "cancel") => void;
  type: TicketCreationType;
  templateId?: string;
  childData?: {
    customerId: string;
    siteId: string;
    customerName: string;
    siteName: string;
    parentId: string;
    userId?: string;
    userName?: string;
  };
  /** Phase 5: opens the drawer with the "Make Recurring" toggle pre-enabled. */
  initialMakeRecurring?: boolean;
  /** Phase 5: renders the toggle disabled (Control Center entry point + edit mode). */
  lockMakeRecurringToggle?: boolean;
  /** Phase 6: when set, the drawer is in edit mode for the given recurring ticket. */
  editMappingId?: string;
  /** Phase 6: pre-fill values for the Duplicate flow. */
  initialValues?: Partial<CreateRecurringTicketPayload>;
  /** Phase 6: "create" or "duplicate" — purely informational, used by telemetry. */
  createDrawerMode?: "create" | "duplicate";
  /**
   * Entry point the drawer is being opened from. Forwarded as `created_from`
   * on the recurring-ticket create payload so the backend can branch on
   * immediate-ticket creation.
   *   "ticket_drawer"     — normal tickets screen (default) — creates an
   *                         immediate ticket alongside the recurring schedule.
   *   "recurring_tickets" — Control Center → Recurring Tickets page — only
   *                         schedules; no immediate ticket.
   */
  createdFrom?: "recurring_tickets" | "ticket_drawer";
  /** List item data from the table row — used in edit mode to populate customer/user
   *  names since the GET /:id detail response does not include joined names. */
  editListItemData?: RecurringTicketListItem;
}) => {
  const theme = useTheme();
  // Duplicate uses editMappingId to fetch data, but behaves as create mode for UI.
  const isEditMode = !!editMappingId && createDrawerMode !== "duplicate";

  const setChecklist = useSetAtom(setChecklistData);
  const setAttachment = useSetAtom(setAttachmentData);
  const saveAssigneeToStore = useSetAtom(setAssigneeData);
  // Assignees live in a Jotai atom (outside RHF). Track a baseline snapshot so
  // we can flag the form as dirty whenever the atom diverges from it. The
  // baseline is written in lockstep with `saveAssigneeToStore` during edit
  // hydration, so the detail-load transition never looks "dirty".
  const currentAssignees = useAtomValue(assigneeData);
  const assigneesBaselineRef = useRef<string>(JSON.stringify(currentAssignees));
  const assigneesDirty =
    JSON.stringify(currentAssignees) !== assigneesBaselineRef.current;
  const globalGeneralConfig = useGlobalStore(
    (state: any) => state.generalConfig,
  );

  const resetStore = useSetAtom(resetTicketStore);
  const { mutateAsync: getTicketTemplate } = useGetTicketTemplateByIdMutation();
  const { mutateAsync: createTicket, isLoading: isTicketSubmitting } =
    useCreateTicket();
  const { mutateAsync: createChildTicket, isLoading: isChildCreateTicket } =
    useCreateChildTicket();

  const { mutateAsync: createTemplate, isLoading: isTemplateSubmitting } =
    useCreateTicketTemplate();

  const { mutateAsync: updateTemplate, isLoading: isTemplateUpdating } =
    useUpdateTicketTemplate();

  // reset the store onunmount
  useEffect(() => {
    return () => {
      resetStore();
    };
  }, []);

  const scheduleData = useAtomCallback(
    useCallback((get) => {
      return get(assigneeData).apiData;
    }, []),
  );

  const attachmentData = useAtomCallback(
    useCallback((get) => {
      return get(getAttachmentData).apiData;
    }, []),
  );
  const isBillableDefault = useGlobalStore((state) => state.isBillableDefault);
  // MSP timezone (the company's tz from settings) — used for recurring
  // ticket payloads. Distinct from `state.timezone` which is the resource's
  // own viewing timezone.
  const companyTimezone = useGlobalStore((state) => state.mspTimezone);
  const schema = yup.object({
    customer: yup.mixed().test((value: any, context) => {
      const { createError } = context;
      if (type === "templateEdit" || type === "templateCreate") return true;

      if (!value && !value?.mapping_id) {
        return createError({ message: "Customer is required" });
      }
      return true;
    }),
    summary: yup
      .string()
      .trim()
      .required("Summary is required")
      .max(250, "Summary must be 250 characters or less"),
    user: yup.mixed().test((value, context) => {
      const { createError } = context;
      if (type === "templateEdit" || type === "templateCreate") return true;

      if (!value) {
        return createError({ message: "User is required" });
      }
      return true;
    }),
    templateName: yup.string().test((value, context) => {
      const { createError } = context;
      if (type !== "templateEdit" && type !== "templateCreate") return true;
      if (!value || value.trim() === "") {
        return createError({ message: "Template name is required" });
      }
      // Check length (max 300 characters)
      if (value.length > 250) {
        return createError({
          message: "Template name must be 250 characters or less",
        });
      }

      return true;
    }),
    priority: yup.string().required("Priority is required").default("low"),
    ticket_site_id: yup.string().nullable(),
    board: yup.string().nullable(),
    status: yup.string().nullable(),
    impact: yup.string().nullable(),
    urgency: yup.string().nullable(),
    budgetedHours: yup.string().nullable(),
    ticketType: yup.string().nullable(),
    ticketSubType: yup.string().nullable(),
    estimatedDateAndTime: yup.string().nullable(),
    contract: yup.string().nullable(),
    sla: yup.string().nullable(),
    tags: yup.array(),
    checklist: yup.array(),
    isBillable: yup.boolean().default(isBillableDefault ?? true),
    // description: yup.string().trim().required("Description is required"),
    //@ts-ignore
    description: yup.string().nullable(),
    // .richTextRequired("Description is required"),
    parentId: yup.string().nullable(),
    showUser: yup.boolean().default(false),
    showParticipants: yup.boolean().default(false),
    ccMembers: yup
      .array()
      .of(
        yup.object({
          user_id: yup.string(),
          member_type: yup
            .string()
            .oneOf(["resource", "customer", "external"])
            .required(),
          display_name: yup.string().nullable(),
          first_name: yup.string(),
          last_name: yup.string(),
          email: yup.string(),
        }),
      )
      .default([]),
  });

  // Merge the recurring section schema unconditionally. All recurring rules
  // are gated by `.when("makeRecurring", ...)`, so they only apply when the
  // toggle is on — this lets users enable recurring dynamically from the
  // sidebar and still get full RHF/yup validation on submit.
  // Pass MSP tz so "today" comparisons happen in the company's timezone.
  const effectiveSchema = mergeRecurringSectionSchema(schema, companyTimezone);

  const methods = useForm({
    defaultValues: {
      customer: "",
      summary: "",
      user: "",
      priority: "low",
      ticket_site_id: "",
      board: globalGeneralConfig?.defaultTicketBoardId,
      status: status.NEW,
      contract: "",
      sla: "",
      impact: "",
      urgency: "",
      budgetedHours: "",
      ticketType: "",
      ticketSubType: "",
      estimatedDateAndTime: "",
      tags: [],
      checklist: [],
      isBillable: isBillableDefault ?? true,
      description: "",
      parentId: "",
      templateName: "",
      showUser: false,
      // Phase 5: recurring fields. `makeRecurring` is the toggle. The rest are
      // populated from `initialValues` (Duplicate) or from the fetched detail
      // record (Edit).
      makeRecurring: initialMakeRecurring,
      recurring_frequency: initialValues?.frequency ?? "",
      recurring_creation_time: initialValues?.creation_time ?? "",
      recurring_start_date: initialValues?.start_date ?? "",
      recurring_end_date: initialValues?.end_date ?? "",
      recurring_time_zone: initialValues?.time_zone ?? "",
      showParticipants: false,
      ccMembers: [],
    },
    resolver: yupResolver(effectiveSchema),
  });

  // Phase 5 — submit hook for the recurring branch. Instantiated unconditionally
  // so hook order stays stable across renders; only invoked when `makeRecurring`.
  const { submitRecurring, isLoading: isRecurringSubmitting } =
    useRecurringTicketFormSubmit();

  // Phase 6 — edit mode: fetch the detail record and seed the form on arrival.
  const { data: editDetailData, isLoading: isEditDetailLoading } =
    useGetRecurringTicketById(editMappingId, {
      enabled: !!editMappingId,
    });

  // Phase 6 — pause/resume/delete actions from the drawer header.
  // Direct API calls for pause and resume (no confirmation modal); delete
  // still routes through `useRecurringTicketActions.openDelete` → dialog.
  const recurringActions = useRecurringTicketActions();
  const { mutateAsync: pauseRecurringTicket, isLoading: isPausing } =
    usePauseRecurringTicket();
  const { mutateAsync: resumeRecurringTicket, isLoading: isResuming } =
    useResumeRecurringTicket();
  const { mutateAsync: skipNextCycle, isLoading: isSkipping } =
    useBulkSkipNextCycleRecurringTickets();
  const isPauseResumeLoading = isPausing || isResuming || isSkipping;
  // Derive current status: prefer the authoritative detail response, fall back
  // to the list row so the icon shows the correct state immediately on open.
  const recurringStatus =
    editDetailData?.data?.status ?? editListItemData?.status ?? null;
  const isPaused = recurringStatus === "paused";

  const handlePauseResumeClick = async () => {
    if (!editMappingId) return;
    try {
      if (isPaused) {
        await resumeRecurringTicket({ mappingId: editMappingId });
        addMixpanelEvent({ event: "recurring_ticket_resumed" });
        toast.success("Recurring ticket resumed");
      } else {
        await pauseRecurringTicket({ mappingId: editMappingId });
        addMixpanelEvent({ event: "recurring_ticket_paused" });
        toast.success("Recurring ticket paused");
      }
    } catch (error) {
      const message =
        (error as { message?: string } | undefined)?.message ||
        (isPaused
          ? "Could not resume recurring ticket"
          : "Could not pause recurring ticket");
      toast.error(message);
    }
  };

  const handleSkipNextOccurrenceClick = async () => {
    if (!editMappingId) return;
    try {
      await skipNextCycle({ recurringTicketIds: [editMappingId] });
      addMixpanelEvent({ event: "recurring_ticket_next_run_skipped" });
      toast.success("Next occurrence skipped");
    } catch (error) {
      const message =
        (error as { message?: string } | undefined)?.message ||
        "Could not skip next occurrence";
      toast.error(message);
    }
  };

  // Immediately seed from list item data (available on mount) so the form is
  // usable while the detail API loads. Only the main form fields are set here;
  // sidebar data (description, checklists, tags, etc.) arrives with the detail.
  useEffect(() => {
    if (!editMappingId || !editListItemData) return;
    const li = editListItemData;
    methods.reset({
      ...methods.getValues(),
      customer: li.customer_id
        ? {
            mapping_id: li.customer_id,
            customer_name: li.customer_name ?? "",
            site_id: li.site_id ?? "",
            site_name: li.site_name ?? "",
          }
        : "",
      user: li.customer_user_id
        ? {
            mapping_id: li.customer_user_id,
            first_name: li.user_first_name ?? "",
            last_name: li.user_last_name ?? "",
            user_name:
              `${li.user_first_name ?? ""} ${li.user_last_name ?? ""}`.trim() ||
              "",
            site_id: li.site_id ?? "",
          }
        : "",
      ticket_site_id: li.site_id ?? "",
      summary: li.ticket_summary ?? "",
      priority: li.priority ?? "low",
      board: li.board_id ?? "",
      makeRecurring: true,
      recurring_frequency: li.frequency ?? "",
      recurring_creation_time: li.creation_time ?? "",
      // Backend already returns end-of-day MSP-tz UTC ISO; the form mirrors
      // that shape (canonical pattern — see ContractInfoSection).
      recurring_start_date: li.start_date ?? "",
      recurring_end_date: li.end_date ?? "",
      recurring_time_zone: li.time_zone ?? "",
    } as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMappingId, editListItemData]);

  // When detail API arrives, layer on the remaining fields (description,
  // sidebar data, checklists, tags, etc.) without resetting what was already set.
  useEffect(() => {
    if (!editDetailData?.data || !editMappingId) return;
    const d = editDetailData.data;
    const li = editListItemData;
    methods.reset({
      ...methods.getValues(),
      // Re-set all fields from detail (superset of list data).
      customer: d.customer_id
        ? {
            mapping_id: d.customer_id,
            customer_name: li?.customer_name ?? "",
            site_id: d.site_id ?? li?.site_id ?? "",
            site_name: li?.site_name ?? "",
          }
        : "",
      user: d.customer_user_id
        ? {
            mapping_id: d.customer_user_id,
            first_name: li?.user_first_name ?? "",
            last_name: li?.user_last_name ?? "",
            user_name:
              `${li?.user_first_name ?? ""} ${li?.user_last_name ?? ""}`.trim() ||
              "",
            site_id: d.site_id ?? li?.site_id ?? "",
          }
        : "",
      ticket_site_id: d.site_id ?? d.ticket_site_id ?? "",
      summary: d.ticket_summary ?? "",
      description: d.ticket_description ?? "",
      priority: d.priority ?? "low",
      urgency: d.urgency ?? "",
      impact: d.impact ?? "",
      sla: d.sla_id ?? "",
      contract: d.contract_id ?? "",
      isBillable: d.is_billable ?? true,
      // Backend ships budgeted_hours as any of: "HH:MM:SS", "HH:MM", "4",
      // { hours, minutes, seconds }, {}, or null (BUG-07). Normalise through
      // parseBudgetedHours → triple → convertToHours → decimal string, which
      // is what the budgetedHours form field stores and getGeneratedData
      // converts back to "HH:MM:SS" on submit.
      budgetedHours: (() => {
        const parsed = parseBudgetedHours(d.budgeted_hours);
        return parsed ? (convertToHours(parsed) ?? "") : "";
      })(),
      board: d.board_id ?? "",
      ticketType: d.ticket_type_id ?? "",
      ticketSubType: d.ticket_subtype_id ?? "",
      makeRecurring: true,
      recurring_frequency: d.frequency ?? "",
      recurring_creation_time: d.creation_time ?? "",
      // Backend already returns end-of-day MSP-tz UTC ISO; form mirrors that shape.
      recurring_start_date: d.start_date ?? "",
      recurring_end_date: d.end_date ?? "",
      recurring_time_zone: d.time_zone ?? "",
      tags: d.tags ?? [],
      checklist: (d.checklists ?? []).map((c) => ({
        checklist_item_name: c.checklist_item_name ?? "",
        order: c.order ?? 0,
        is_important: c.is_important ?? false,
        is_completed: c.is_completed ?? false,
      })),
    } as Record<string, unknown>);
    if (d.checklists?.length) {
      // Atom shape is `{ items: [...] }` — must wrap the array so consumers
      // reading `checklistData.items` don't get `undefined`.
      setChecklist({
        items: d.checklists.map((c) => ({
          checklist_item_name: c.checklist_item_name ?? "",
          order: c.order ?? 0,
          is_important: c.is_important ?? false,
          is_completed: c.is_completed ?? false,
        })),
      });
    }

    // Hydrate existing assignees into the Jotai atom so the AvatarGroup +
    // AssigneeButton reflect them in edit mode.
    //
    // Backend API inconsistency (by design, not a bug):
    //   - Normal tickets  → `schedulings` — assignees WITH per-shift
    //     start_time/end_time (each assignee can have a schedule).
    //   - Recurring tickets → `assignees` — plain list of resources, no
    //     per-assignee schedule times (recurring doesn't support shifts).
    // Both payloads share the same display fields (names, avatars) and
    // `resource_id`, which is all this atom needs — so we read from
    // `schedulings` first, then fall back to `assignees`.
    //
    // Field-name variance (assignee_*, user_*, plain) is handled by the
    // `pick()` helper below.
    type Scheduling = {
      resource_id?: string;
      mapping_id?: string;
      first_name?: string;
      last_name?: string;
      profile_image?: string | null;
      assignee_first_name?: string;
      assignee_last_name?: string;
      assignee_profile_image?: string | null;
      user_first_name?: string;
      user_last_name?: string;
      user_profile_image?: string | null;
      schedule_times?: Array<{
        start_time?: string | null;
        end_time?: string | null;
      }>;
    };
    const rawSource = (
      Array.isArray(d.schedulings) && d.schedulings.length > 0
        ? (d.schedulings as Scheduling[])
        : Array.isArray(d.assignees)
          ? (d.assignees as Scheduling[])
          : []
    ) as Scheduling[];

    const pick = (...vals: Array<string | null | undefined>) =>
      vals.find((v) => v != null && v !== "") ?? "";

    if (rawSource.length > 0) {
      const apiNew = rawSource.map((s) => ({
        uuid: uuid(),
        mapping_id: s.mapping_id,
        first_name: pick(
          s.first_name,
          s.assignee_first_name,
          s.user_first_name,
        ),
        last_name: pick(s.last_name, s.assignee_last_name, s.user_last_name),
        profile_image: pick(
          s.profile_image,
          s.assignee_profile_image,
          s.user_profile_image,
        ),
        resource_id: s.resource_id ?? "",
        schedule_times: (s.schedule_times ?? [{}]).map((t) => ({
          schedule_uuid: uuid(),
          start_time: t?.start_time ?? null,
          end_time: t?.end_time ?? null,
        })),
      }));
      const tableData = rawSource.map((s, idx) => ({
        uuid: apiNew[idx].uuid,
        mapping_id: s.mapping_id,
        resource_id: s.resource_id ?? "",
        first_name: apiNew[idx].first_name,
        last_name: apiNew[idx].last_name,
        profile_image: apiNew[idx].profile_image,
        start_time: s.schedule_times?.[0]?.start_time ?? null,
        end_time: s.schedule_times?.[0]?.end_time ?? null,
        errors: { start_time: null, end_time: null },
      }));
      const hydrationValue = {
        apiData: { new: apiNew, updated: [], deleted: [] },
        tableData,
      };
      // Update baseline BEFORE writing the atom so the dirty check never
      // flips true as a side effect of the hydration itself.
      assigneesBaselineRef.current = JSON.stringify(hydrationValue);
      saveAssigneeToStore(hydrationValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDetailData, editMappingId]);
  // for child ticket set values to form state
  useEffect(() => {
    if (childData && type === "child") {
      // @ts-ignore
      methods.setValue("customer", {
        mapping_id: childData.customerId,
        customer_name: childData.customerName,
        site_id: childData.siteId,
        site_name: childData.siteName,
      });
      methods.setValue("parentId", childData.parentId);
      methods.setValue("ticket_site_id", childData.siteId);
    }
  }, [childData]);

  useEffect(() => {
    if (templateId && type === "templateEdit") {
      handleTemplateSelect(templateId);
    }
  }, [templateId]);

  const handleTemplateSelect = async (selectedTemplate: string) => {
    try {
      const templateData = await getTicketTemplate(selectedTemplate);
      methods.setValue("templateName", templateData?.template_name);
      methods.setValue("description", templateData?.template_description);
      methods.setValue("summary", templateData?.template_summary);
      methods.setValue("board", templateData?.board);
      methods.setValue("priority", templateData?.priority);
      methods.setValue("isBillable", templateData?.is_billable);
      methods.setValue("urgency", templateData?.urgency);
      methods.setValue("ticketType", templateData?.ticket_type);
      methods.setValue("ticketSubType", templateData?.ticket_sub_type);
      methods.setValue("impact", templateData?.impact);
      // templateData?.sla && methods.setValue("sla", templateData?.sla);
      methods.setValue("tags", templateData?.tags);
      methods.setValue("checklist", templateData?.checklist);
      methods.setValue(
        "budgetedHours",
        convertToHours(templateData?.budgeted_hours) || "",
      );
      methods.setValue("showUser", templateData?.show_user);
      methods.setValue("status", templateData?.ticket_status);

      setChecklist({ items: templateData?.checklist || [] });
      if (templateData?.attachments && templateData?.attachments?.length > 0) {
        const attachmentArray = templateData.attachments?.map(
          (attachment: any) => {
            return {
              key: attachment.mapping_id,
              name: attachment.original_name,
              progress: 100,
              size: parseInt(attachment.size),
              type: attachment.file_type,
              status: "Uploaded",
              responseData: attachment,
            };
          },
        );
        setAttachment(attachmentArray);
      }
    } catch (error) {}
  };

  const submit = methods.handleSubmit(
    async (data) => {
      // Phase 5 — recurring branch.
      // Two flows:
      //   a) Normal tickets screen (createdFrom="ticket_drawer") + NEW record:
      //      call the normal create-ticket API with recurring fields inlined
      //      (`is_recurring_ticket: true`, frequency, creation_time, etc.).
      //      Backend creates the immediate ticket + schedules the recurring run
      //      in one call — avoids the previous two-step delay.
      //   b) Control Center (createdFrom="recurring_tickets") OR edit flow:
      //      keep routing through `submitRecurring` (recurring-tickets API).
      if (data.makeRecurring) {
        const isEditing = !!editMappingId && createDrawerMode !== "duplicate";
        const useNormalTicketApi =
          createdFrom === "ticket_drawer" && !isEditing;

        if (useNormalTicketApi) {
          try {
            const generatedData = await getGeneratedData(data, type);
            await createTicket({
              ...(generatedData as CreateTicketInput),
              is_recurring_ticket: true,
              frequency: data.recurring_frequency,
              creation_time: data.recurring_creation_time,
              // Form already holds end-of-day MSP-tz UTC ISO (snapped at the
              // DateInput change handler — see RecurringTicketFormSection).
              start_date: (data.recurring_start_date as string) || "",
              end_date: (data.recurring_end_date as string) || null,
              time_zone: companyTimezone || "UTC",
            });
            addMixpanelEvent({ event: "recurring_ticket_created" });
            toast.success("New ticket created successfully");
            onClose({}, "save");
          } catch (e: unknown) {
            const message =
              (e as { message?: string } | undefined)?.message ||
              "Unable to create ticket";
            toast.error(message);
          }
          return;
        }

        try {
          // Reuse the exact same payload builder as normal ticket create.
          const generatedTicketData = await getGeneratedData(data, type);
          await submitRecurring(generatedTicketData, {
            editMappingId:
              createDrawerMode === "duplicate" ? undefined : editMappingId,
            recurringFields: {
              frequency: data.recurring_frequency,
              creation_time: data.recurring_creation_time,
              start_date: data.recurring_start_date,
              end_date: data.recurring_end_date || null,
            },
            dirtyFields: methods.formState.dirtyFields as Record<
              string,
              boolean
            >,
            createdFrom,
          });
          // Fire the created event only when this is a new create (or duplicate
          // — which is a new recurring ticket). Skip for edit updates.
          if (!isEditing) {
            addMixpanelEvent({ event: "recurring_ticket_created" });
          }
          onClose({}, "save");
        } catch (e: unknown) {
          const message =
            (e as { message?: string } | undefined)?.message ||
            "Unable to save recurring ticket";
          if (!String(message).toLowerCase().includes("no changes to save")) {
            toast.error(message);
          }
        }
        return;
      }

      const generatedData = await getGeneratedData(data, type);
      try {
        switch (type) {
          case "ticket":
            // Create ticket
            // @ts-ignore
            await createTicket(generatedData);
            toast.success("New ticket created successfully");
            break;
          case "templateCreate":
            await createTemplate({
              ...generatedData,
            });
            toast.success("Template created successfully");
            break;

          case "templateEdit":
            await updateTemplate({
              templateId,
              ...generatedData,
            });
            toast.success("Template updated successfully");
            break;

          case "child":
            // Child ticket
            // @ts-ignore
            createChildTicket({ ticketId: data.parentId, ...generatedData });
            toast.success("Child ticket created successfully");
            break;
          default:
            break;
        }
        onClose({}, "save");
      } catch (e: any) {
        switch (type) {
          case "templateCreate":
            toast.error(e?.message || "Unable to create template");
            break;
          case "templateEdit":
            toast.error(e?.message || "Unable to update template");
        }
      }
    },
    (errors) => {
      console.log(errors);
      // Auto-scroll the "Select frequency" container into view if any of its
      // fields has a validation error. (MUI date/time pickers don't expose a
      // queryable `name` attribute on the DOM, so we scroll the parent.)
      const recurringFieldKeys = [
        "recurring_frequency",
        "recurring_creation_time",
        "recurring_start_date",
        "recurring_end_date",
      ];
      const hasRecurringError = recurringFieldKeys.some(
        (key) => (errors as Record<string, unknown>)[key],
      );
      if (!hasRecurringError) return;
      const node = document.getElementById("recurring-fields-container");
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
  );

  const getGeneratedData = async (data: any, type: TicketCreationType) => {
    const attachments = attachmentData();
    const checklist = await sortChecklistData(data.checklist);
    const schedules = scheduleData();
    const newSchedules = schedules.new.map((schedule: any) => {
      const transformedScheduleTimes = schedule.schedule_times
        .map((time: any) => {
          if (time.start_time && time.end_time) {
            return {
              start_time: time.start_time,
              end_time: time.end_time,
            };
          } else {
            return null;
          }
        })
        .filter((time: any) => time !== null);

      return {
        ...schedule,
        schedule_times: transformedScheduleTimes,
      };
    });

    const generatedData = {
      isBillable: data.isBillable,
      ...(data.board && { board: data.board }),
      status: data.status,
      priority: data.priority,
      description:
        data.description && data.description.length > 0
          ? data.description
          : data.summary,
      summary: data.summary,
      urgency: data.urgency,

      ticketType: data.ticketType,
      ticketSubtype: data.ticketSubType,
      // source,
      impact: data.impact,
      sla: data.sla,
      // @ts-ignore
      customer: data.customer?.mapping_id,
      budgetedHours: data.budgetedHours
        ? convertDoubletoHourAndMinutes(data.budgetedHours, "colons") ||
          "00:00:00"
        : "00:00:00",
      site: data.user.site_id,
      ticket_site_id: data.ticket_site_id,
      user: data.user.mapping_id,
      contract: data.contract,
      schedule: newSchedules,
      checklist: checklist,
      attachments: attachments,
      tags: data.tags,
      estimatedEndTime: data.estimatedDateAndTime
        ? data.estimatedDateAndTime
        : null,
      ...(data.showParticipants && { ccMembers: data.ccMembers }),
    };

    const childData = {
      ...generatedData,
      ticketId: data.parentId,
    };

    const templateData = {
      ...generatedData,
      templateName: data.templateName,
      showUser: data.showUser,
    };
    delete templateData.site;
    delete templateData.ticket_site_id;
    delete templateData.customer;
    delete templateData.user;
    delete templateData.sla;
    delete templateData.ccMembers;

    switch (type) {
      case "ticket":
        return generatedData;
      case "templateCreate":
        return templateData;
      case "templateEdit":
        return templateData;
      case "child":
        return childData;
      default:
        return generatedData;
    }
  };

  return (
    <Drawer
      open={open}
      // @ts-ignore
      onClose={onClose}
      title={
        initialMakeRecurring || methods.watch("makeRecurring")
          ? isEditMode
            ? "Edit recurring ticket"
            : "Create recurring ticket"
          : getDrawerTitle(type)
      }
      headerRightActionComponent={
        isEditMode && editMappingId ? (
          <Stack direction="row" gap="16px" alignItems="center">
            {isPauseResumeLoading ? (
              <CircularLoader
                size="16px"
                sx={{
                  "&.MuiCircularProgress-root": {
                    color: theme.palette.neutral["020"],
                  },
                }}
              />
            ) : null}
            <Tooltip
              title={
                isPaused ? "Resume recurring ticket" : "Pause recurring ticket"
              }
              arrow
            >
              <span style={{ display: "inline-flex" }}>
                {isPaused ? (
                  <PlayIcon
                    sx={{
                      color: theme.palette.lightBg.main,
                      cursor: isPauseResumeLoading ? "default" : "pointer",
                      pointerEvents: isPauseResumeLoading ? "none" : "auto",
                      opacity: isPauseResumeLoading ? 0.5 : 1,
                    }}
                    style={{ width: "24px", height: "24px" }}
                    onClick={() => {
                      void handlePauseResumeClick();
                    }}
                  />
                ) : (
                  <PauseIcon
                    sx={{
                      color: theme.palette.lightBg.main,
                      cursor: isPauseResumeLoading ? "default" : "pointer",
                      pointerEvents: isPauseResumeLoading ? "none" : "auto",
                      opacity: isPauseResumeLoading ? 0.5 : 1,
                    }}
                    style={{ width: "24px", height: "24px" }}
                    onClick={() => {
                      void handlePauseResumeClick();
                    }}
                  />
                )}
              </span>
            </Tooltip>
            <Tooltip
              title={
                recurringStatus !== "active"
                  ? "Only active tickets can skip the next occurrence"
                  : "Skip next occurrence"
              }
              arrow
            >
              <span style={{ display: "inline-flex" }}>
                <SkipNextIcon
                  sx={{
                    color: theme.palette.lightBg.main,
                    cursor:
                      isPauseResumeLoading || recurringStatus !== "active"
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      isPauseResumeLoading || recurringStatus !== "active"
                        ? 0.5
                        : 1,
                  }}
                  style={{ width: "24px", height: "24px" }}
                  onClick={() => {
                    if (isPauseResumeLoading || recurringStatus !== "active")
                      return;
                    void handleSkipNextOccurrenceClick();
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip title="Delete recurring ticket" arrow>
              <span style={{ display: "inline-flex" }}>
                <DeleteIcon
                  sx={{
                    color: theme.palette.lightBg.main,
                    cursor: "pointer",
                  }}
                  style={{ width: "24px", height: "24px" }}
                  onClick={() => {
                    recurringActions.openDelete(editMappingId);
                  }}
                />
              </span>
            </Tooltip>
          </Stack>
        ) : undefined
      }
      onPrimaryButtonClick={submit}
      footer={
        <CustomerFooter
          isLoading={
            isTicketSubmitting ||
            isChildCreateTicket ||
            isTemplateSubmitting ||
            isTemplateUpdating ||
            isRecurringSubmitting
          }
          methods={methods}
          onSubmit={submit}
          onClose={onClose}
          type={type}
          // BUG-12: in edit mode, disable Save when the form is clean.
          disableSaveWhenClean={isEditMode}
          isEditMode={isEditMode}
          editMappingId={editMappingId}
          assigneesDirty={assigneesDirty}
        />
      }
    >
      {/* <TicketJotaiProvider> */}
      {
        <FormProvider {...methods}>
          <Stack
            sx={{
              //   minWidth: "688px",
              height: "100%",
              overflow: "hidden",
              flex: 1,
            }}
            direction={"row"}
          >
            <Stack
              sx={{
                width: "600px",
                height: "100%",
                overflow: "scroll",
                backgroundColor: theme.palette.neutral["005"],
                gap: "8px",
              }}
            >
              <CreateTicketMainForm
                type={type}
                lockMakeRecurringToggle={lockMakeRecurringToggle}
              />
            </Stack>
            {/* side drawer — disabled while detail API is loading (edit/duplicate) */}
            <Stack
              sx={{
                ...(editMappingId && isEditDetailLoading
                  ? { opacity: 0.5, pointerEvents: "none" }
                  : {}),
              }}
            >
              <CreateTicketSidebarComponent
                type={type}
                lockMakeRecurringToggle={lockMakeRecurringToggle}
                createdFrom={createdFrom}
              />
            </Stack>
          </Stack>
        </FormProvider>
      }
      {/* </TicketJotaiProvider> */}
    </Drawer>
  );
};

const CustomerFooter = ({
  onClose,
  onSubmit,
  methods,
  isLoading = false,
  type,
  disableSaveWhenClean = false,
  isEditMode = false,
  editMappingId,
  assigneesDirty = false,
}: {
  onClose: (
    e: React.MouseEvent<HTMLButtonElement>,
    type?: "save" | "cancel",
  ) => void;
  onSubmit: () => void;
  methods: UseFormReturn<any>;
  isLoading?: boolean;
  type: TicketCreationType;
  /** BUG-12: when true, disables Save while `formState.dirtyFields` is empty. */
  disableSaveWhenClean?: boolean;
  isEditMode?: boolean;
  /** Truthy in both edit and duplicate flows — used to skip customer-default
   *  overrides that would clobber values hydrated from an existing template. */
  editMappingId?: string;
  /** Parent-tracked dirty flag for the Jotai assignees atom (outside RHF). */
  assigneesDirty?: boolean;
}) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const { watch, control, setValue, formState } = methods;
  // Subscribe to dirtyFields so the component re-renders when dirty state changes.
  // Without this read, the disableSaveWhenClean check in the button never re-evaluates.
  const dirtyFieldCount = Object.keys(formState.dirtyFields ?? {}).length;

  const watchCustomer = watch("customer");

  const { data: customerContract } = useGetCustomerContractSettings(
    watchCustomer?.mapping_id || "",
  );

  const { data: customerData } = useGetCustomerById(
    watchCustomer?.mapping_id || "",
  );

  const { data: defaultContractData } = useGetBilligDefaultContract(
    watchCustomer?.mapping_id,
    "ticket",
  );

  useEffect(() => {
    // Skip when editing/duplicating a recurring ticket — the saved sla/contract
    // were already hydrated from the detail response and must not be clobbered
    // by the customer's defaults.
    if (editMappingId) return;
    if (customerData) {
      setValue("sla", customerData?.default_sla_id);
    }
    if (defaultContractData) {
      setValue("contract", defaultContractData?.mapping_id);
    }
  }, [customerData, defaultContractData, editMappingId]);

  useEffect(() => {
    const watchSubscription = watch((data) => {
      if (
        (type === "templateCreate" || type === "templateEdit") &&
        data.templateName &&
        data.summary
        // &&
        // data.description
      ) {
        setIsDisabled(false);
        return;
      }
      if (
        data.customer &&
        data.customer?.mapping_id &&
        data.user &&
        data.user?.mapping_id &&
        data.summary
        //  &&
        // data.description
      ) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    });

    return () => {
      watchSubscription.unsubscribe();
    };
  }, [watch]);
  const theme = useTheme();
  return (
    <Stack
      direction={"row"}
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        p: "12px 16px",
        borderTop: `1px solid ${theme.palette.neutral["020"]}`,
        position: "relative",
      }}
    >
      {type !== "templateCreate" && type !== "templateEdit" ? (
        <>
          <AssigneeBanner customerId={watchCustomer?.mapping_id} />
          <TicketAssigneeButton
            customerId={watchCustomer?.mapping_id}
            isRecurring={Boolean(watch("makeRecurring"))}
          />
        </>
      ) : (
        <Stack direction={"row"} gap="4px" alignItems={"center"}>
          <Controller
            render={({ field }) => (
              <CustomCheckBox
                {...field}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
            name="showUser"
            control={control}
          />{" "}
          <Typography variant="body-medium" color={theme.palette.lightBg.low}>
            Display templates for users
          </Typography>
        </Stack>
      )}

      <Stack direction={"row"} gap={"8px"}>
        <TicketAttachmentButton />
        <Button disabled={isLoading} variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          loading={isLoading}
          disabled={
            isDisabled ||
            (disableSaveWhenClean && dirtyFieldCount === 0 && !assigneesDirty)
          }
          variant="primary"
          onClick={onSubmit}
          aria-label={isEditMode ? "Save changes" : "Create ticket"}
        >
          {" "}
          {isEditMode ? "Save" : type === "templateEdit" ? "Update" : "Create"}
        </Button>
      </Stack>
    </Stack>
  );
};
const sortChecklistData = async (checklistData: Array<Record<string, any>>) => {
  if (!checklistData || checklistData.length === 0) {
    return [];
  }
  const filteredChecklistItems = checklistData
    .filter(
      (item: any) =>
        item.checklist_item_name !== null && item.checklist_item_name !== "",
    )
    .map((item: any, index: number) => ({
      checklist_item_name: item.checklist_item_name,
      order: index,
      is_important: item.is_important,
    }));
  return [...filteredChecklistItems];
};

const getDrawerTitle = (type: TicketCreationType) => {
  switch (type) {
    case "ticket":
      return "Create New Ticket";
    case "templateCreate":
      return "Create New Ticket Template";
    case "templateEdit":
      return "Update Ticket Template";
    case "child":
      return "Create New Child Ticket";
    default:
      return "Create New Ticket";
  }
};
